import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import { FoodDisplay } from "../modal-components/FoodDisplay";
import { FoodActions } from "../modal-components/FoodActions";
import {
  customRound,
  getFilteredFoodsForAddModal,
  reCalculateMacros,
} from "../../../../../utils/nutrition-util";
import SvgIcon from "../../../../../parts/SvgIcon";
import { RecentFoods } from "../modal-components/RecentFoods";

export const AddFoodModal = ({ locals, api }) => {
  const { mostUsedMealPlans, mealPlan, query } = locals;
  const { weeks = [] } = mealPlan;
  const { week = 1, mealPlannerDate, mealPlannerMeal, athlete } = query;
  const selectedWeek = weeks[week - 1]?.days || [];
  const filteredFoodsOfMeal = getFilteredFoodsForAddModal(
    selectedWeek,
    mealPlannerDate,
    mealPlannerMeal
  );

  const initialState = {
    searchFilter: "all",
    addedItems: filteredFoodsOfMeal,
  };
  const initialFoodDisplayState = {
    saveAsNewMeal: false,
    mealName: "",
    mealDescription: "",
  };

  const initialFoodActionState = {
    selectedItem: null,
    unitOfMeasure: "Select",
    unitOfMeasureOptions: [],
    isSearching: false,
    searchValue: "",
  };

  const [foodDisplayState, setFoodDisplayState] = useState(
    initialFoodDisplayState
  );
  const foodDisplayRef = useRef(null);
  const foodActionsRef = useRef(null);
  const [state, setState] = useState(initialState);
  const [foodActionState, setFoodActionState] = useState(
    initialFoodActionState
  );
  const { saveAsNewMeal } = foodDisplayState;

  const { searchFilter, addedItems } = state;
  const mostUsedToUse = mostUsedMealPlans[searchFilter];

  const handleExternalItemSelect = (item) => {
    setTimeout(() => {
      foodActionsRef.current?.focusQuantityInput();
    }, 100);
  };

  // UseEffect to know when saveAsNewMeal is true and focus to the input
  useEffect(() => {
    if (saveAsNewMeal) {
      setTimeout(() => {
        foodDisplayRef.current?.focusMealName();
      }, 100);
    }
  }, [saveAsNewMeal]);

  /**
   * Function to update the state of the component
   * @param {*} newState
   * @returns
   */
  const updateState = (newState) =>
    setState((prevState) => ({ ...prevState, ...newState }));

  /**
   * open create-new-food modal or data-source modal
   */
  const handleOpenModal = (destination) => {
    const shouldConfirm =
      addedItems?.length > 0 && filteredFoodsOfMeal?.length === 0;

    if (
      !shouldConfirm ||
      (process.browser &&
        confirm(
          `Are you sure you want to leave this page? Any food you've added will be lost`
        ))
    ) {
      submit(`/actions/refresh/?`, {
        method: "GET",
        body: {
          "modal[type]": destination,
        },
      });
    }
  };

  /**
   * Insert foods in the day
   */
  const handleCreateMeal = () => {
    if (mealPlannerDate && mealPlannerMeal) {
      const { mealName, mealDescription } = foodDisplayState;
      const isNewMeal = saveAsNewMeal && !!mealName.trim();

      const foodsWithMealFormat = addedItems.map(
        ({ type, _id, customQuantityNumber, customUnitOfMeasure }) => ({
          type: type || "food",
          foodId: _id,
          quantityNumber: customQuantityNumber || 1,
          unitOfMeasure: customUnitOfMeasure,
        })
      );
      const { _id: mealPlanId } = mealPlan;
      const weekIndex = week - 1;

      let body = {
        weekIndex,
        mealType: mealPlannerMeal,
        date: mealPlannerDate,
        foods: foodsWithMealFormat,
        bodyAction: "handle-food",
        handle: "add",
        isTemplate: athlete ? false : true,
      };
      let url = `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`;

      if (isNewMeal) {
        body = {
          ...body,
          name: mealName,
          description: !!mealDescription.trim()
            ? mealDescription
            : "description",
        };
        url = `/actions/meal-planner/update-days-meal-plan-and-add-meal?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`;
      }
      submit(url, {
        method: "POST",
        body,
      });
    }
  };

  /**
   * Callback to handle the updates of the item (quantity)
   */
  const handleUpdateItems = useCallback(
    (index, updatedItem) => {
      updateState({
        addedItems: addedItems.map((item, i) =>
          i === index ? { ...item, ...updatedItem } : item
        ),
      });
    },
    [addedItems]
  );

  /**
   * Function to add an item to the display component
   * @param {*} item
   */

  const handleAddItem = useCallback(
    (items) => {
      updateState({ addedItems: [...addedItems, ...items] });
    },
    [addedItems]
  );

  /**
   * Function to remove an item of the display component
   * @param {*} index
   */

  const handleRemoveItem = useCallback(
    (index) => {
      const updatedItems = [...addedItems];
      updatedItems.splice(index, 1);
      updateState({ addedItems: updatedItems });
    },
    [addedItems]
  );

  /**
   * Function to change the option in the tabs
   * @param {*} filter
   */
  const handleChangeTab = (filter) => {
    updateState({ searchFilter: filter });
    setFoodActionState(initialFoodActionState);
  };

  /**
   * Calculate the total of the Nutritional values
   */
  const totalOfNutritionalValues = useMemo(() => {
    return addedItems.reduce(
      (acc, item) => {
        const updatedValues = reCalculateMacros({
          itemDetails: {
            itemProteins: item.proteins || 0,
            itemCarbs: item.carbs || 0,
            itemFats: item.fats || 0,
            itemCalories: item.calories || 0,
            quantityNumber: item.quantityNumber || 1,
            customQuantityNumber: item.customQuantityNumber || 1,
            measurementsValue: item.measurementsValue || 1,
            customUnitOfMeasure: item.customUnitOfMeasure || "custom",
            type: item.type,
            skipRecalculation: item.skipRecalculation,
          },
        });

        acc.totalProteins += updatedValues.proteins;
        acc.totalCarbs += updatedValues.carbs;
        acc.totalFats += updatedValues.fats;
        acc.totalCalories += updatedValues.calories;

        return acc;
      },
      { totalCalories: 0, totalCarbs: 0, totalProteins: 0, totalFats: 0 }
    );
  }, [addedItems]);

  const { totalCalories, totalCarbs, totalProteins, totalFats } =
    totalOfNutritionalValues;

  return (
    <Modal className={`add-food-modal`} title={"Food"}>
      <div className="body">
        <div className="header-buttons">
          <div className="first-row">
            <nav role="tablist">
              <div className="buttons-options">
                {["all", "recipes", "meals", "foods"].map((filter) => (
                  <a
                    key={filter}
                    id={filter}
                    onClick={() => handleChangeTab(filter)}
                    aria-current={searchFilter === filter ? true : undefined}
                  >
                    <span>
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </span>
                  </a>
                ))}
              </div>
              <hgroup className="create-new">
                <button
                  data-button-type={"outline"}
                  onClick={() => handleOpenModal("create-new-food")}
                >
                  <span>Create New</span>
                </button>
              </hgroup>
            </nav>
          </div>
        </div>
        <div className="main-container">
          <div className="add-food-main-container">
            <FoodActions
              ref={foodActionsRef}
              locals={locals}
              searchFilter={searchFilter}
              api={api}
              onAddItem={handleAddItem}
              foodActionState={foodActionState}
              setFoodActionState={setFoodActionState}
            />
            <div className="added-text">
              <span className="clipped font-semibold h-5">Added</span>
            </div>

            <FoodDisplay
              ref={foodDisplayRef}
              state={foodDisplayState}
              setState={setFoodDisplayState}
              items={addedItems}
              location={searchFilter}
              onRemoveItem={handleRemoveItem}
              onUpdateItems={handleUpdateItems}
            />
          </div>
          <div className="recent-foods-main-container">
            <RecentFoods
              mostUsedToUse={mostUsedToUse}
              location={searchFilter}
              setFoodActionState={setFoodActionState}
              onItemSelect={handleExternalItemSelect}
            />
          </div>
        </div>

        <div className="footer">
          <div className="data-source">
            <button
              data-button-type="secondary"
              onClick={() => handleOpenModal("data-source")}
            >
              <SvgIcon src="/img/new-icons/settings.svg" alt="" />
              <span>Data Source</span>
            </button>
            {addedItems.length > 0 && searchFilter !== "meals" && (
              <div className="checkbox-container">
                <SvgIcon
                  noFilterOnLight={saveAsNewMeal}
                  onClick={() => {
                    setFoodDisplayState((prevState) => ({
                      ...prevState,
                      saveAsNewMeal: !prevState.saveAsNewMeal,
                    }));
                  }}
                  src={`/img/new-icons/${
                    saveAsNewMeal
                      ? "checked-square.svg"
                      : "unchecked-square.svg"
                  }`}
                />
                <span>Save as new meal</span>
              </div>
            )}
          </div>
          <div className="nutrition-button-container">
            <div className="footer-nutrition">
              <div>
                <span className="mr-2 kcal">kCal</span>
                <span>{customRound(totalCalories)}</span>
              </div>
              <div>
                <span className="mr-2 protein">Protein</span>
                <span>{customRound(totalProteins)}g</span>
              </div>
              <div>
                <span className="mr-2 carbs">Carbs</span>
                <span>{customRound(totalCarbs)}g</span>
              </div>
              <div>
                <span className="mr-2 fat">Fat</span>
                <span>{customRound(totalFats)}g</span>
              </div>
            </div>
            <div className="button-modal">
              <a
                role="button"
                data-button-type="secondary"
                data-action-cancel
                className="button alt"
                href={`/actions/refresh?modal=`}
              >
                Cancel
              </a>
              <button
                data-button-type="primary"
                onClick={handleCreateMeal}
                disabled={
                  addedItems.length === 0 ||
                  addedItems.some(
                    ({ customQuantityNumber }) =>
                      customQuantityNumber === "" || isNaN(customQuantityNumber)
                  )
                }
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

AddFoodModal.propTypes = {
  locals: PropTypes.object,
  api: PropTypes.any,
};
